import _defineProperty from "@babel/runtime/helpers/defineProperty";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import React, { useEffect, useLayoutEffect as useRealLayoutEffect, useMemo, useRef, useContext } from 'react';
import PropTypes from 'prop-types';
export var Context = /*#__PURE__*/React.createContext();
export var useListViewed = () => {
  return useContext(Context);
};

if (process.browser) {
  require('intersection-observer');
}

var useLayoutEffect = process.browser ? useRealLayoutEffect : useEffect;
var ListViewedProvider = /*#__PURE__*/React.memo(function ListViewedProvider(_ref) {
  var children = _ref.children,
      root = _ref.root,
      _ref$rootMargin = _ref.rootMargin,
      rootMargin = _ref$rootMargin === void 0 ? '0px 0px 0px 0px' : _ref$rootMargin,
      id = _ref.id,
      threshold = _ref.threshold,
      onItemViewed = _ref.onItemViewed;
  var observer = useRef();
  var itemsMap = useRef();

  if (itemsMap.current == null) {
    itemsMap.current = new Map();
  }

  useLayoutEffect(() => {
    var disconnecting = false;

    var handleChange = entries => {
      if (disconnecting) {
        return;
      }

      entries.forEach(entry => {
        var isIntersecting = entry.isIntersecting && entry.intersectionRatio > 0;

        if (isIntersecting) {
          var itemInfo = itemsMap.current.get(entry.target);

          if (itemInfo) {
            if (onItemViewed && !itemInfo.isViewed) {
              var itemInfoViewed = _objectSpread(_objectSpread({}, itemInfo), {}, {
                isViewed: true
              });

              itemsMap.current.set(entry.target, itemInfoViewed);
              onItemViewed({
                itemInfo: itemInfoViewed,
                id,
                entry
              });
            }
          }
        }
      });
    };

    observer.current = new IntersectionObserver(handleChange, {
      root,
      rootMargin,
      threshold
    });
    return () => {
      disconnecting = true;
      observer.current.disconnect();
    };
  }, [id, root, rootMargin, threshold, onItemViewed]);
  var context = useMemo(() => {
    return {
      observe(node, itemInfo) {
        itemsMap.current.set(node, itemInfo);
        return observer.current.observe(node);
      },

      unobserve(node) {
        itemsMap.current.delete(node);
        return observer.current.unobserve(node);
      }

    };
  }, []);
  return /*#__PURE__*/React.createElement(Context.Provider, {
    value: context
  }, children);
});
ListViewedProvider.propTypes = {
  /**
   * The `action` option to dispatch when an item is in view
   */
  children: PropTypes.node,
  id: PropTypes.string,

  /**
   * The `onItemViewed` option is a callback to be triggered once the item is on view.
   */
  onItemViewed: PropTypes.func.isRequired,

  /**
   * The `root` option to pass to the IntersectionObserver. It must be a DOM
   * node, not a ref (so that updating it triggers a re-render). If not passed,
   * the browser viewport is used.
   */
  root: PropTypes.object,

  /**
   * The `rootMargin` option to pass to the IntersectionObserver. It can be
   * used to extend or contract the area that triggers what gets set as seen.
   */
  rootMargin: PropTypes.string,

  /**
   * The `threshold` option to pass to the IntersectionObserver. An array of
   * values (between 0 and 1.0). Represents distance needed to cover to trigger callback.
   */
  threshold: PropTypes.array
};
ListViewedProvider.Context = Context;

var ListViewedProviderEntry = _ref2 => {
  var children = _ref2.children,
      data = _ref2.data;

  var _useListViewed = useListViewed(),
      observe = _useListViewed.observe,
      unobserve = _useListViewed.unobserve;

  var ref = useRef();
  useEffect(() => {
    var node = ref.current;

    if (node && data) {
      observe(node, data);
    }

    return () => unobserve(node);
  }, [observe, unobserve, data]);
  return /*#__PURE__*/React.createElement("div", {
    ref: ref
  }, children);
};

ListViewedProviderEntry.propTypes = {
  children: PropTypes.node,
  data: PropTypes.any
};
ListViewedProvider.Entry = ListViewedProviderEntry;
export default ListViewedProvider;